import { createSlice } from '../factory/slice-factory';
import { getExplorerData } from '@repo/api';
import { runWithDedup } from '../utils';
const initialState = {
    data: null,
    loading: true,
    error: null,
    isUpdatingAccess: false,
    updatingAccessType: null,
};
export const explorerSlice = createSlice({
    name: 'explorer',
    initialState,
    persist: { keys: [] },
    actions: ({ setSlice, get }) => {
        let accessUpdateRequestId = 0;
        /**
         * Helper function to check if user is logged in
         */
        const checkIsLoggedIn = () => {
            const state = get();
            // Check webAuth (web platform) or auth (mobile/platform) slice
            return Boolean(state?.webAuth?.isLoggedIn ??
                state?.auth?.user ??
                state?.auth?.token ??
                state?.mobileAuth?.isLoggedIn);
        };
        /**
         * Helper function to update explorer data and sync isBlurRequired
         */
        const updateExplorerData = (data, error = null) => {
            setSlice({
                data,
                loading: false,
                error,
            });
            // Sync isBlurRequired in profile slice
            const state = get();
            state?.profile?.syncIsBlurRequired?.();
        };
        return {
            /**
             * Initialize explorer state based on login status
             * Should be called after store rehydration to set loading to false if user is not logged in
             */
            initializeExplorer: () => {
                const isLoggedIn = checkIsLoggedIn();
                if (!isLoggedIn) {
                    setSlice({ loading: false, data: null, error: null });
                }
            },
            /**
             * Fetch explorer data from API
             */
            fetchExplorerData: async () => {
                const key = 'explorer.fetchExplorerData';
                // Check if user is logged in
                const isLoggedIn = checkIsLoggedIn();
                // If user is not logged in, set loading to false immediately
                if (!isLoggedIn) {
                    updateExplorerData(null);
                    return;
                }
                setSlice({ loading: true, error: null });
                try {
                    // Use deduplication to prevent multiple simultaneous calls
                    const data = await runWithDedup(key, () => getExplorerData());
                    updateExplorerData(data);
                }
                catch (error) {
                    updateExplorerData(null, error.message || 'Failed to fetch explorer data');
                }
            },
            startAccessUpdate: (expectedAccessType) => {
                if (!expectedAccessType)
                    return;
                const isLoggedIn = checkIsLoggedIn();
                if (!isLoggedIn) {
                    // No-op if user is not logged in.
                    return;
                }
                accessUpdateRequestId += 1;
                const requestId = accessUpdateRequestId;
                setSlice({
                    isUpdatingAccess: true,
                    updatingAccessType: expectedAccessType,
                });
                const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
                void (async () => {
                    const delayMs = 3000;
                    const maxAttempts = 4;
                    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
                        // Cancelled/restarted by a newer request
                        if (requestId !== accessUpdateRequestId)
                            return;
                        // Stop if user logged out mid-flight
                        if (!checkIsLoggedIn()) {
                            setSlice({ isUpdatingAccess: false, updatingAccessType: 'failed' });
                            return;
                        }
                        try {
                            // Intentionally NOT deduped: this is a poll loop.
                            const data = (await getExplorerData());
                            updateExplorerData(data);
                            if ((data?.accessType ?? '') === expectedAccessType) {
                                setSlice({ isUpdatingAccess: false, updatingAccessType: null });
                                return;
                            }
                        }
                        catch (error) {
                            // Keep polling; store the error message for observability.
                            setSlice({ error: error?.message || 'Failed to fetch explorer data' });
                        }
                        if (attempt < maxAttempts) {
                            await delay(delayMs);
                        }
                    }
                    // Exhausted attempts without matching expected access type.
                    if (requestId !== accessUpdateRequestId)
                        return;
                    setSlice({ isUpdatingAccess: false, updatingAccessType: null });
                })();
            },
            /**
             * Clear explorer data (e.g., on logout)
             */
            clearExplorerData: () => {
                updateExplorerData(null);
                setSlice({ isUpdatingAccess: false, updatingAccessType: null });
            },
            /**
             * Manually set explorer data
             */
            setExplorerData: (data) => {
                updateExplorerData(data);
            },
            /**
             * Add a building to accessBuildingsData
             * Used when a user adds a building report
             */
            addBuildingToAccessData: (building) => {
                const state = get();
                const currentData = state?.explorer?.data;
                if (!currentData) {
                    return;
                }
                const updatedData = {
                    ...currentData,
                    accessBuildingsData: [...(currentData.accessBuildingsData ?? []), building],
                };
                updateExplorerData(updatedData);
            },
        };
    },
});
